import { editScheduleFiltersActions } from '../actions'
import { useEditScheduleGuard, useAlertClassIds } from '../hooks'
import { useActions, useAppSelector } from '../../../store/hooks'
import { editSchedulePageSelector } from '../../../store/slices/editSchedulePage'
import { referencesSelector } from '../../../store/slices/references'

export function useEditScheduleFilters() {
    const { alertClassIds, clearAlertClasses } = useAlertClassIds()

    const dateFrom = useAppSelector(editSchedulePageSelector.dateFrom)
    const dateTo = useAppSelector(editSchedulePageSelector.dateTo)
    const viewMode = useAppSelector(editSchedulePageSelector.viewMode)
    const selectedGroupId = useAppSelector(editSchedulePageSelector.selectedGroupId)
    const selectedTeacherId = useAppSelector(editSchedulePageSelector.selectedTeacherId)
    const selectedBuildId = useAppSelector(editSchedulePageSelector.selectedBuildId)
    const selectedRoomId = useAppSelector(editSchedulePageSelector.selectedRoomId)
    const selectedClassTypeId = useAppSelector(editSchedulePageSelector.selectedClassTypeId)
    const selectedSubjectId = useAppSelector(editSchedulePageSelector.selectedSubjectId)
    const showMoreOptions = useAppSelector(editSchedulePageSelector.showMoreOptions)
    const isValidGroupValue = useAppSelector(editSchedulePageSelector.isValidGroupValue)
    const isValidTeacherValue = useAppSelector(editSchedulePageSelector.isValidTeacherValue)
    const groupOptions = useAppSelector(referencesSelector.groupOptions)
    const teacherOptions = useAppSelector(referencesSelector.teacherOptions)
    const buildingOptions = useAppSelector(referencesSelector.buildingOptions)
    const roomOptions = useAppSelector(referencesSelector.roomOptions)
    const classTypeOptions = useAppSelector(referencesSelector.classTypeOptions)
    const subjectOptions = useAppSelector(referencesSelector.subjectOptions)

    const {
        resetSchedule,
        referenceLoadAudience,
        setDateRange,
        clearDateFilter,
        setViewMode,
        setSelectedGroupId,
        setSelectedTeacherId,
        setSelectedBuildId,
        setSelectedRoomId,
        setSelectedClassTypeId,
        setSelectedSubjectId,
        resetFilters,
        toggleShowMoreOptions,
        resetEditor,
    } = useActions(editScheduleFiltersActions)

    const { checkBeforeAction } = useEditScheduleGuard(editScheduleFiltersActions)

    const guardedFilterChange = (type, payload, applyChange) => {
        if (!checkBeforeAction(type, payload)) return

        clearAlertClasses()
        applyChange(payload)
    }

    const changeViewMode = (mode) => {
        if (!checkBeforeAction('changeMode', mode)) return

        clearAlertClasses()
        setViewMode(mode)
        resetSchedule()
        resetEditor()
    }

    const selectBuild = (value) => {
        guardedFilterChange('setSelectedBuildId', value, (nextValue) => {
            setSelectedBuildId(nextValue)

            if (!nextValue) {
                referenceLoadAudience(null)
                return
            }

            referenceLoadAudience(nextValue)
        })
    }

    const handleClearDateFilter = () => {
        if (!checkBeforeAction('clearDateFilter')) return

        clearAlertClasses()
        clearDateFilter()
    }

    const handleResetFilters = () => {
        if (!checkBeforeAction('resetFilters')) return

        clearAlertClasses()
        resetFilters()
        referenceLoadAudience(null)
    }

    return {
        dateFrom,
        dateTo,
        viewMode,
        selectedGroupId,
        selectedTeacherId,
        selectedBuildId,
        selectedRoomId,
        selectedClassTypeId,
        selectedSubjectId,
        showMoreOptions,
        isValidGroupValue,
        isValidTeacherValue,
        groupOptions,
        teacherOptions,
        buildingOptions,
        roomOptions,
        classTypeOptions,
        subjectOptions,
        alertClassIds,
        setDateRange: (range) => guardedFilterChange('setDateRange', range, setDateRange),
        clearDateFilter: handleClearDateFilter,
        changeViewMode,
        setSelectedGroupId: (value) => guardedFilterChange('setSelectedGroupId', value, setSelectedGroupId),
        setSelectedTeacherId: (value) => guardedFilterChange('setSelectedTeacherId', value, setSelectedTeacherId),
        selectBuild,
        setSelectedRoomId: (value) => guardedFilterChange('setSelectedRoomId', value, setSelectedRoomId),
        setSelectedClassTypeId: (value) => guardedFilterChange('setSelectedClassTypeId', value, setSelectedClassTypeId),
        setSelectedSubjectId: (value) => guardedFilterChange('setSelectedSubjectId', value, setSelectedSubjectId),
        resetFilters: handleResetFilters,
        toggleShowMoreOptions,
    }
}
